"use client";

import { useCallback, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Agent } from "app-types/agent";
import { AgentNavigation } from "lib/navigation/agent-navigation";
import { AgentContextService } from "lib/services/agent-context-service";
import { AgentCleanupService } from "lib/services/agent-cleanup-service";
import {
  useAgentManagement,
  useAgentThreads,
  useAgentSelection,
} from "./use-agent-store";

/**
 * Hook for agent-aware navigation
 */
export function useAgentNavigation() {
  const router = useRouter();
  const pathname = usePathname();
  const { selectAgent } = useAgentManagement();
  const { agentThreads, setAgentThread } = useAgentThreads();
  const { selectAgentWithThread } = useAgentSelection();

  // Parse current agent context from URL
  const currentContext = AgentNavigation.parseAgentContext(pathname);

  // Initialize context from URL and storage on mount
  useEffect(() => {
    const initContext = AgentContextService.initializeContext(pathname);

    if (initContext.shouldRestoreAgent && initContext.agentId) {
      // Load thread mapping from storage
      const threadMapping = AgentContextService.loadAgentThreadMapping();
      const storedThreadId = threadMapping[initContext.agentId]?.threadId;

      // Update store with restored context
      if (storedThreadId) {
        setAgentThread(initContext.agentId, storedThreadId);
      }
    }
  }, [pathname, setAgentThread]);

  /**
   * Navigate to main chat
   */
  const navigateToMainChat = useCallback(() => {
    // Cleanup previous agent context
    const previousAgent = currentContext.agentId
      ? ({ id: currentContext.agentId } as Agent)
      : undefined;

    AgentCleanupService.setCurrentAgent(null);
    AgentContextService.cleanupContext(previousAgent);

    selectAgent(null);
    router.push(AgentNavigation.getMainChatUrl());
  }, [router, selectAgent, currentContext]);

  /**
   * Navigate to agent chat (new conversation)
   */
  const navigateToAgent = useCallback(
    (agent: Agent) => {
      // Set current agent for cleanup service
      AgentCleanupService.setCurrentAgent(agent);

      // Save agent context
      AgentContextService.saveAgentContext(agent);
      AgentContextService.updateBrowserHistory(agent);

      selectAgent(agent);
      router.push(AgentNavigation.getAgentChatUrl(agent.id));
    },
    [router, selectAgent],
  );

  /**
   * Navigate to specific agent thread
   */
  const navigateToAgentThread = useCallback(
    (agent: Agent, threadId: string) => {
      // Save agent context with thread
      AgentContextService.saveAgentContext(agent, threadId);
      AgentContextService.updateBrowserHistory(agent, threadId);

      selectAgentWithThread(agent, threadId);
      router.push(AgentNavigation.getAgentThreadUrl(agent.id, threadId));
    },
    [router, selectAgentWithThread],
  );

  /**
   * Navigate to main chat thread
   */
  const navigateToMainThread = useCallback(
    (threadId: string) => {
      // Cleanup agent context when going to main chat
      AgentContextService.clearAgentContext();
      AgentContextService.updateBrowserHistory(null, threadId);

      selectAgent(null);
      router.push(AgentNavigation.getMainChatThreadUrl(threadId));
    },
    [router, selectAgent],
  );

  /**
   * Get the appropriate navigation URL for an agent
   * Returns existing thread URL if available, otherwise new chat URL
   */
  const getAgentNavigationUrl = useCallback(
    (agent: Agent): string => {
      const existingThreadId = agentThreads[agent.id];
      return existingThreadId
        ? AgentNavigation.getAgentThreadUrl(agent.id, existingThreadId)
        : AgentNavigation.getAgentChatUrl(agent.id);
    },
    [agentThreads],
  );

  /**
   * Handle thread creation for agent context
   */
  const handleAgentThreadCreated = useCallback(
    (agent: Agent, threadId: string) => {
      // Save to store and context service
      setAgentThread(agent.id, threadId);
      AgentContextService.saveAgentContext(agent, threadId);
      AgentContextService.updateBrowserHistory(agent, threadId);

      // Update URL if we're currently in this agent's context
      if (currentContext.agentId === agent.id && !currentContext.threadId) {
        router.replace(AgentNavigation.getAgentThreadUrl(agent.id, threadId));
      }
    },
    [setAgentThread, currentContext, router],
  );

  /**
   * Check if currently in agent context
   */
  const isInAgentContext = currentContext.isAgentChat;

  /**
   * Check if currently in main chat context
   */
  const isInMainContext = AgentNavigation.isMainChatPath(pathname);

  /**
   * Get breadcrumbs for current navigation context
   */
  const getBreadcrumbs = useCallback(
    (agent?: Agent) => {
      return AgentNavigation.getBreadcrumbs(pathname, agent);
    },
    [pathname],
  );

  return {
    // Navigation actions
    navigateToMainChat,
    navigateToAgent,
    navigateToAgentThread,
    navigateToMainThread,

    // Utility functions
    getAgentNavigationUrl,
    handleAgentThreadCreated,
    getBreadcrumbs,

    // Context information
    currentContext,
    isInAgentContext,
    isInMainContext,
  };
}
